import { db } from '@/database/Database';
import jsPDF from 'jspdf';

/**
 * TranscriptionService - AI-powered audio transcription
 * 
 * Captures audio from WebRTC streams and generates transcripts using OpenAI Whisper
 * Integrates with virtual room sessions for automatic transcription
 */

export interface TranscriptionSegment {
  text: string;
  timestamp: number;
  speaker?: string;
}

export interface TranscriptionResult {
  segments: TranscriptionSegment[];
  fullText: string;
  duration: number;
  language?: string;
}

export class TranscriptionService {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private isRecording = false;
  private startTime = 0;
  private transcriptSegments: TranscriptionSegment[] = [];

  /**
   * Start recording audio from media stream
   */
  async startRecording(stream: MediaStream): Promise<void> {
    if (this.isRecording) {
      console.warn('[Transcription] Already recording');
      return;
    }

    try {
      // Create audio-only stream
      const audioStream = new MediaStream(
        stream.getAudioTracks()
      );

      // Create MediaRecorder with optimal settings for speech
      this.mediaRecorder = new MediaRecorder(audioStream, {
        mimeType: this.getSupportedMimeType(),
        audioBitsPerSecond: 128000, // Good quality for speech
      });

      this.audioChunks = [];
      this.startTime = Date.now();

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
          console.log('[Transcription] Audio chunk recorded:', event.data.size, 'bytes');
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('[Transcription] MediaRecorder error:', event);
      };

      // Record in 10-second chunks for incremental transcription
      this.mediaRecorder.start(10000);
      this.isRecording = true;

      console.log('[Transcription] Recording started');
    } catch (error) {
      console.error('[Transcription] Failed to start recording:', error);
      throw error;
    }
  }

  /**
   * Stop recording and get audio blob
   */
  async stopRecording(): Promise<Blob | null> {
    if (!this.isRecording || !this.mediaRecorder) {
      console.warn('[Transcription] Not recording');
      return null;
    }

    return new Promise((resolve) => {
      this.mediaRecorder!.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { 
          type: this.getSupportedMimeType() 
        });
        
        console.log('[Transcription] Recording stopped. Total size:', audioBlob.size, 'bytes');
        
        this.isRecording = false;
        this.mediaRecorder = null;
        
        resolve(audioBlob);
      };

      this.mediaRecorder!.stop();
    });
  }

  /**
   * Transcribe audio blob using OpenAI Whisper
   */
  async transcribe(audioBlob: Blob): Promise<TranscriptionResult> {
    console.log('[Transcription] Starting transcription...', audioBlob.size, 'bytes');

    try {
      // Get Supabase function URL
      const functionUrl = this.getSupabaseFunctionUrl();
      if (!functionUrl) {
        throw new Error('Supabase function URL not configured');
      }

      // Convert blob to base64 for JSON transport
      const base64Audio = await this.blobToBase64(audioBlob);

      // Call Supabase Edge Function for transcription
      const response = await fetch(`${functionUrl}/transcribe-audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          audio: base64Audio,
          mimeType: audioBlob.type,
          language: 'en', // Can be made configurable
        }),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Transcription failed: ${error}`);
      }

      const result = await response.json();
      
      console.log('[Transcription] Transcription complete:', result);

      // Add to segments
      if (result.text) {
        this.transcriptSegments.push({
          text: result.text,
          timestamp: Date.now() - this.startTime,
        });
      }

      return {
        segments: this.transcriptSegments,
        fullText: this.transcriptSegments.map(s => s.text).join(' '),
        duration: Date.now() - this.startTime,
        language: result.language || 'en',
      };
    } catch (error) {
      console.error('[Transcription] Transcription error:', error);
      throw error;
    }
  }

  /**
   * Get current transcript
   */
  getCurrentTranscript(): TranscriptionResult {
    return {
      segments: this.transcriptSegments,
      fullText: this.transcriptSegments.map(s => s.text).join(' '),
      duration: Date.now() - this.startTime,
    };
  }

  /**
   * Clear transcript
   */
  clearTranscript(): void {
    this.transcriptSegments = [];
    this.audioChunks = [];
    this.startTime = 0;
  }

  /**
   * Check if currently recording
   */
  isRecordingActive(): boolean {
    return this.isRecording;
  }

  /**
   * Get supported MIME type for recording
   */
  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        console.log('[Transcription] Using MIME type:', type);
        return type;
      }
    }

    console.warn('[Transcription] No optimal MIME type supported, using default');
    return 'audio/webm';
  }

  /**
   * Convert Blob to base64
   */
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Get Supabase function URL
   */
  private getSupabaseFunctionUrl(): string | null {
    const override = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL?.replace(/\/$/, '');
    if (override) return override;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, '');
    if (!supabaseUrl) return null;

    return `${supabaseUrl}/functions/v1`;
  }
}

/**
 * Save transcript to database
 */
import { db } from '@/database/Database';
import jsPDF from 'jspdf';

export async function saveTranscript(
  roomId: string,
  transcript: TranscriptionResult,
  metadata?: {
    roomTitle?: string;
    participants?: string[];
    classId?: string;
    categoryId?: string;
    userId?: string;
  }
): Promise<void> {
  try {
    console.log('[Transcription] Saving transcript...', {
      roomId,
      segments: transcript.segments.length,
      duration: transcript.duration,
    });

    const payload = {
      roomId,
      transcript,
      metadata,
      savedAt: new Date().toISOString(),
    };

    if (!metadata?.userId) {
      console.warn('[Transcription] No userId provided, skipping DB save');
      return;
    }

    // Save directly to meeting_transcripts table (separate from calendar items)
    const id = `transcript_${roomId}_${Date.now()}`;
    await db.query(
      'INSERT INTO meeting_transcripts (id, user_id, title, content, meeting_date, created_at) VALUES (?, ?, ?, ?, ?, ?)',
      [
        id,
        metadata.userId,
        metadata.roomTitle || 'Meeting Transcript',
        JSON.stringify(payload),
        new Date().toISOString(),
        new Date().toISOString(),
      ]
    );
    console.log('[Transcription] Transcript saved to meeting_transcripts table');
  } catch (error) {
    console.error('[Transcription] Failed to save transcript:', error);
    throw error;
  }
}

/**
 * Export transcript as text file
 */
export function exportTranscriptAsText(transcript: TranscriptionResult, filename: string = 'transcript.txt'): void {
  const content = [
    '=== VIRTUAL ROOM TRANSCRIPT ===',
    '',
    `Duration: ${Math.floor(transcript.duration / 1000 / 60)} minutes`,
    `Language: ${transcript.language || 'en'}`,
    '',
    '=== FULL TRANSCRIPT ===',
    '',
    transcript.fullText,
    '',
    '=== SEGMENTS ===',
    '',
    ...transcript.segments.map((seg, i) => 
      `[${Math.floor(seg.timestamp / 1000)}s] ${seg.speaker || 'Speaker'}: ${seg.text}`
    ),
  ].join('\n');

  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);

  console.log('[Transcription] Transcript exported:', filename);
}

/**
 * Export transcript as JSON
 */
export function exportTranscriptAsJSON(
  transcript: TranscriptionResult,
  metadata?: any,
  filename: string = 'transcript.json'
): void {
  const data = {
    transcript,
    metadata,
    exportedAt: new Date().toISOString(),
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);

  console.log('[Transcription] Transcript exported as JSON:', filename);
}

/**
 * Export transcript as PDF (simple text layout)
 */
export function exportTranscriptAsPDF(
  transcript: TranscriptionResult,
  metadata?: any,
  filename: string = 'transcript.pdf'
): void {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const lineHeight = 16;
  let y = 40;

  const addLine = (text: string, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    const split = doc.splitTextToSize(text, 520);
    doc.text(split, 40, y);
    y += split.length * lineHeight;
  };

  addLine('Virtual Room Transcript', true);
  if (metadata?.roomTitle) addLine(`Room: ${metadata.roomTitle}`);
  addLine(`Duration: ${(transcript.duration / 1000).toFixed(0)}s`);
  if (metadata?.participants?.length) addLine(`Participants: ${metadata.participants.join(', ')}`);
  addLine('');

  addLine('Full Transcript', true);
  addLine(transcript.fullText || '(no text yet)');
  addLine('');

  addLine('Segments', true);
  transcript.segments.forEach((seg) => {
    addLine(`[${Math.floor(seg.timestamp / 1000)}s] ${seg.speaker || 'Speaker'}: ${seg.text}`);
  });

  doc.save(filename);
  console.log('[Transcription] Transcript exported as PDF:', filename);
}
